import { useState } from 'react'
import { BadgeCheck, Send } from 'lucide-react'
import { DetailFields } from '../../../core/index.js'
import type { FeedbackAdminApi } from '../../adminApi.js'
import type { ChangeRequestView } from '../../types.js'
import { changeTitle, isSha, latestRequirement } from './changeText.js'
import { StepDialog } from './StepDialog.js'

type AdminApi = Pick<FeedbackAdminApi, 'approveChange' | 'acceptChange'>

interface GateDialogProps {
  api: AdminApi
  change: ChangeRequestView
  onClose: () => void
}

const INPUT_CLASS = 'mt-1 w-full border border-marine/30 rounded px-2 py-1 font-mono text-xs font-normal normal-case tracking-normal text-ink bg-white focus:outline-none focus:ring-2 focus:ring-coral'

/** Gate 2: the root approves the exact version shown, by its hash; a later edit cannot ride on this approval. */
export function ApproveDialog({ api, change, onClose }: GateDialogProps) {
  const latest = latestRequirement(change)
  const [baseSha, setBaseSha] = useState('')
  const trimmed = baseSha.trim()
  const baseValid = trimmed === '' || isSha(trimmed)
  if (!latest) {
    return null
  }
  return (
    <StepDialog
      eyebrow="Gate 2 · Root only"
      title="Approve and send"
      subtitle={changeTitle(change)}
      confirmLabel={`Approve version ${latest.version}`}
      confirmIcon={Send}
      canConfirm={baseValid}
      run={() => api.approveChange(change.changeRequestId, { requirementVersion: latest.version, contentHash: latest.contentHash, ...(trimmed ? { baseSha: trimmed } : {}) })}
      onClose={onClose}
    >
      <p className="text-xs text-ink-mid">
        You are approving this exact text. Editing it afterwards is refused; any change means returning it to draft and approving a new version.
      </p>
      <DetailFields
        fields={[
          { label: 'Requirement version', value: String(latest.version) },
          { label: 'Content hash', value: latest.contentHash, mono: true },
        ]}
      />
      <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid">
        Base commit of main <span className="font-normal normal-case text-ink-mid/70">Optional</span>
        <input value={baseSha} onChange={(event) => setBaseSha(event.target.value)} placeholder="e.g. bec698d" className={INPUT_CLASS} />
      </label>
      {!baseValid && <p className="text-[11px] font-bold text-coral">A commit SHA is 7 to 40 hexadecimal characters.</p>}
    </StepDialog>
  )
}

/** Gate 3: the root accepts the pull request at the commit recorded, which is the commit they reviewed. */
export function AcceptDialog({ api, change, onClose }: GateDialogProps) {
  const pullRequest = change.pullRequest
  if (!pullRequest) {
    return null
  }
  return (
    <StepDialog
      eyebrow="Gate 3 · Root only"
      title="Accept the change"
      subtitle={changeTitle(change)}
      confirmLabel="Accept"
      confirmIcon={BadgeCheck}
      canConfirm
      run={() => api.acceptChange(change.changeRequestId, pullRequest.headSha)}
      onClose={onClose}
    >
      <p className="text-xs text-ink-mid">
        Accepting records the pull request at this commit and completes the linked feedback. Merging stays yours, on GitHub; if you reviewed a later commit, record it
        first.
      </p>
      <DetailFields
        fields={[
          { label: 'Pull request', value: pullRequest.url, mono: true },
          { label: 'Head commit', value: pullRequest.headSha, mono: true },
        ]}
      />
    </StepDialog>
  )
}
